import { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import {
  AddCircle as AddIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  SettingsSuggest as ManageIcon,
} from '@mui/icons-material';
import { ViewInfo } from '@/store/api/tableConfigApi';

interface ViewManagerProps {
  views: ViewInfo[];
  currentView?: string;
  onSwitchView: (viewName?: string) => void;
  onCreateView: (viewName: string) => Promise<void>;
  onRemoveView: (viewName: string) => Promise<void>;
  loading?: boolean;
}

export default function ViewManager({
  views,
  currentView,
  onSwitchView,
  onCreateView,
  onRemoveView,
  loading = false
}: ViewManagerProps) {
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newViewName, setNewViewName] = useState('');
  const [manageDialogOpen, setManageDialogOpen] = useState(false);

  const handleCreateView = async () => {
    if (newViewName.trim()) {
      await onCreateView(newViewName.trim());
      setNewViewName('');
      setCreateDialogOpen(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <FormControl size="small" sx={{ minWidth: 150 }}>
        <InputLabel id="view-select-label">Active View</InputLabel>
        <Select
          labelId="view-select-label"
          value={currentView || 'default'}
          label="Active View"
          onChange={(e) => onSwitchView(e.target.value === 'default' ? undefined : e.target.value)}
          disabled={loading}
        >
          <MenuItem value="default">
            <em>Default View</em>
          </MenuItem>
          {views.filter(view => view.view_name !== 'default').map((view) => (
            <MenuItem key={view.view_name} value={view.view_name}>
              {view.view_name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Tooltip title={loading ? "Loading..." : "Create New View"} sx={{ mb: 5 }}>
        <span>
          <IconButton
            size="small"
            color="primary"
            onClick={() => setCreateDialogOpen(true)}
            disabled={loading}
          >
            <AddIcon fontSize="medium" color={loading ? 'disabled' : 'primary'} />
          </IconButton>
        </span>
      </Tooltip>

      <Tooltip title={loading || views.filter(view => view.view_name !== 'default').length === 0 ? (loading ? "Loading..." : "No custom views available to manage") : "Manage Views"}>
        <span>
          <IconButton
            size="small"
            color="primary"
            onClick={() => setManageDialogOpen(true)}
            disabled={loading || views.filter(view => view.view_name !== 'default').length === 0}
            sx={{padding: '0px 0px 5px 0px'}}
          >
            <ManageIcon
              sx={{ fontSize: '1.7rem' }}
              color={loading || views.filter(view => view.view_name !== 'default').length === 0 ? 'disabled' : 'primary'}
            />
          </IconButton>
        </span>
      </Tooltip>

      {/* Create View Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)}>
        <DialogTitle>Create New View</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Save the current column configuration as a new named view.
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="View Name"
            fullWidth
            variant="outlined"
            value={newViewName}
            onChange={(e) => setNewViewName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateView} variant="contained" disabled={!newViewName.trim()}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manage Views Dialog */}
      <Dialog open={manageDialogOpen} onClose={() => setManageDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Manage Views</DialogTitle>
        <DialogContent dividers>
          <List>
            {/* Always show Default View at the top */}
            <ListItem
              key="default"
              secondaryAction={
                <IconButton
                  edge="end"
                  aria-label="delete"
                  disabled={true}
                  sx={{
                    opacity: 0.5,
                    cursor: 'not-allowed'
                  }}
                >
                  <DeleteIcon fontSize="small" color="disabled" />
                </IconButton>
              }
            >
              <ListItemIcon>
                <ViewIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Default View"
                secondary="Default view cannot be deleted"
              />
            </ListItem>
            
            {/* Show custom views (excluding any 'default' view from backend) */}
            {views.filter(view => view.view_name !== 'default').map((view) => (
              <ListItem
                key={view.view_name}
                secondaryAction={
                  <IconButton
                    edge="end"
                    aria-label="delete"
                    onClick={() => onRemoveView(view.view_name)}
                    sx={{
                      opacity: 1,
                      cursor: 'pointer'
                    }}
                  >
                    <DeleteIcon fontSize="small" color="error" />
                  </IconButton>
                }
              >
                <ListItemIcon>
                  <ViewIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={view.view_name}
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManageDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
